import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { api, useFetch, errMsg, fmtDate } from "@/lib/api";
import { applySiteMeta } from "@/components/SiteMeta";

const TEXT_FIELDS = [
  ["brand_name", "Brand name"], ["hero_eyebrow", "Hero eyebrow"], ["hero_title", "Hero headline"],
  ["hero_subtitle", "Hero sub-text", true], ["hero_image", "Hero image URL"], ["contact_email", "Contact email"], ["footer_text", "Footer text"],
];

const SEO_FIELDS = [
  ["meta_title", "Page title (browser tab & Google)", false, "e.g. PropNest | Property management for UK landlords"],
  ["meta_description", "Meta description (shown under the title in search results, ~155 characters)", true, ""],
  ["meta_keywords", "Meta keywords (comma separated)", false, "landlord software, letting agent software"],
  ["og_image", "Social share image URL (1200×630 recommended)", false, "https://..."],
  ["favicon_url", "Favicon URL (.png or .ico)", false, "https://..."],
];

function ListEditor({ title, items, keys, onChange, testid }) {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="panel space-y-4" data-testid={testid}>
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold">{title}</h3>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, Object.fromEntries(keys.map(([k]) => [k, ""]))])} data-testid={`${testid}-add`}><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid gap-2 border border-slate-200 rounded-lg p-3 relative">
          {keys.map(([k, label, long]) => long
            ? <Textarea key={k} placeholder={label} value={it[k]} onChange={(e) => upd(i, k, e.target.value)} rows={2} />
            : <Input key={k} placeholder={label} value={it[k]} onChange={(e) => upd(i, k, e.target.value)} />)}
          <Button type="button" variant="ghost" size="sm" className="justify-self-end" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-rose-600 mr-1" />Remove</Button>
        </div>
      ))}
    </div>
  );
}

export function AdminSite() {
  const { data } = useFetch("/admin/site");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return <Loading />;
  const save = async (e) => {
    e.preventDefault();
    try {
      const { key, ...body } = f;
      await api.put("/admin/site", body);
      toast.success("Site content published");
      applySiteMeta(body);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} data-testid="admin-site-page">
      <PageHeader eyebrow="Marketing site" title="Site content" subtitle="Edit what visitors see on the public homepage."
        actions={<Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="site-save">Publish changes</Button>} />
      <div className="panel grid gap-4 mb-6">
        {TEXT_FIELDS.map(([k, label, long]) => (
          <div key={k} className="space-y-1.5">
            <Label>{label}</Label>
            {long ? <Textarea rows={3} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`site-${k}`} />
              : <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`site-${k}`} />}
          </div>
        ))}
      </div>
      <div className="panel mb-6 bg-emerald-50 border-emerald-200" data-testid="share-link-panel">
        <h3 className="text-lg font-bold">Link to share on Facebook, LinkedIn & X</h3>
        <p className="text-sm text-slate-600 mt-1">Social networks don't run the app's JavaScript, so use this link when posting. It shows your title, description and share image below, and sends visitors straight to the homepage.</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <Input readOnly value={`${process.env.REACT_APP_BACKEND_URL}/api/share`} className="font-mono text-xs bg-white" data-testid="share-link" />
          <Button type="button" variant="outline" onClick={() => { navigator.clipboard?.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/share`); toast.success("Link copied"); }} data-testid="share-link-copy">Copy</Button>
          <a href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/share`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center px-4 rounded-md border border-slate-300 bg-white text-sm font-medium hover:bg-slate-50" data-testid="share-link-fb-debug">Preview on Facebook</a>
        </div>
        <p className="text-xs text-slate-500 mt-2">After changing these settings, click "Scrape Again" in the Facebook debugger (or LinkedIn Post Inspector) to refresh their cached preview.</p>
      </div>
      <div className="panel grid gap-4 mb-6" data-testid="site-seo">
        <div>
          <h3 className="text-xl font-bold">SEO & meta tags</h3>
          <p className="text-sm text-slate-500">Controls the browser tab title, search result snippet and how links look when shared.</p>
        </div>
        {SEO_FIELDS.map(([k, label, long, ph]) => (
          <div key={k} className="space-y-1.5">
            <Label>{label}</Label>
            {long ? <Textarea rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`site-${k}`} />
              : <Input value={f[k] || ""} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`site-${k}`} />}
            {k === "meta_description" && <p className="text-xs text-slate-500">{(f[k] || "").length} characters</p>}
          </div>
        ))}
      </div>
      <div className="mb-6">
        <ListEditor title="Custom meta tags" testid="site-custom-meta" items={f.custom_meta || []}
          keys={[["name", "Name or property (e.g. google-site-verification, robots, og:locale)"], ["content", "Content"]]}
          onChange={(v) => setF({ ...f, custom_meta: v })} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <ListEditor title="Features" testid="site-features" items={f.features || []} keys={[["title", "Title"], ["text", "Description", true]]} onChange={(v) => setF({ ...f, features: v })} />
        <ListEditor title="FAQs" testid="site-faqs" items={f.faqs || []} keys={[["q", "Question"], ["a", "Answer", true]]} onChange={(v) => setF({ ...f, faqs: v })} />
      </div>
    </form>
  );
}

export function AdminSmtp() {
  const { data } = useFetch("/admin/smtp");
  const { data: log, reload: reloadLog } = useFetch("/admin/emails");
  const [f, setF] = useState(null);
  const [to, setTo] = useState("");
  useEffect(() => {
    if (data) setF({ enabled: false, host: "", port: 587, security: "tls", username: "", from_email: "", from_name: "", ...data, password: "" });
  }, [data]);
  if (!f) return <Loading />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { has_password, key, ...body } = f;
      const r = await api.put("/admin/smtp", { ...body, port: Number(body.port) });
      setF({ ...f, ...r.data, password: "" });
      toast.success("SMTP settings saved");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const test = async () => {
    try { await api.post("/admin/smtp/test", { to }); toast.success("Test email sent"); } catch (err) { toast.error(errMsg(err)); } finally { reloadLog(); }
  };
  return (
    <div data-testid="admin-smtp-page">
      <PageHeader eyebrow="Email" title="Email & SMTP" subtitle="Used for renter welcome packs, password resets and compliance reminders." />
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <form onSubmit={save} className="panel lg:col-span-2 grid sm:grid-cols-2 gap-4" data-testid="smtp-form">
          <div className="sm:col-span-2 flex items-center gap-3"><Switch checked={!!f.enabled} onCheckedChange={(c) => setF({ ...f, enabled: c })} data-testid="smtp-enabled" /><Label>Send real emails</Label></div>
          <div className="space-y-1.5"><Label>SMTP host</Label><Input value={f.host} onChange={set("host")} placeholder="smtp.example.com" data-testid="smtp-host" /></div>
          <div className="space-y-1.5"><Label>Port</Label><Input type="number" value={f.port} onChange={set("port")} data-testid="smtp-port" /></div>
          <div className="space-y-1.5">
            <Label>Security</Label>
            <Select value={f.security} onValueChange={(v) => setF({ ...f, security: v })}>
              <SelectTrigger data-testid="smtp-security"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="tls">STARTTLS (587)</SelectItem><SelectItem value="ssl">SSL/TLS (465)</SelectItem><SelectItem value="none">None</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Username</Label><Input value={f.username} onChange={set("username")} data-testid="smtp-username" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Password {f.has_password && <span className="text-xs text-slate-500">(saved — leave blank to keep)</span>}</Label><Input type="password" value={f.password} onChange={set("password")} data-testid="smtp-password" /></div>
          <div className="space-y-1.5"><Label>From email</Label><Input type="email" value={f.from_email} onChange={set("from_email")} data-testid="smtp-from-email" /></div>
          <div className="space-y-1.5"><Label>From name</Label><Input value={f.from_name} onChange={set("from_name")} data-testid="smtp-from-name" /></div>
          <div className="sm:col-span-2"><Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="smtp-save">Save settings</Button></div>
        </form>
        <div className="panel space-y-3 h-fit">
          <h3 className="text-xl font-bold">Send a test</h3>
          <Input type="email" placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="smtp-test-to" />
          <Button onClick={test} disabled={!to} variant="outline" className="w-full" data-testid="smtp-test-button"><Send className="w-4 h-4 mr-1" />Send test email</Button>
          <p className="text-xs text-slate-500">Save your settings first. While SMTP is off, emails are logged below as "skipped".</p>
        </div>
      </div>
      <h3 className="text-xl font-bold mb-3">Recent emails</h3>
      <div className="panel p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>To</TableHead><TableHead>Subject</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {(log || []).map((l) => (
              <TableRow key={l.id}><TableCell className="whitespace-nowrap">{fmtDate(l.created_at)}</TableCell><TableCell className="text-sm">{l.to}</TableCell><TableCell className="text-sm">{l.subject}</TableCell>
                <TableCell className="text-xs">{l.kind}</TableCell><TableCell><StatusBadge status={l.status} />{l.error && <div className="text-xs text-slate-500 mt-1 max-w-xs truncate" title={l.error}>{l.error}</div>}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        {log?.length === 0 && <p className="text-sm text-slate-500 p-6">No emails yet.</p>}
      </div>
    </div>
  );
}
